/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { FC, Fragment, memo } from 'react';
import type { Page } from '@framework/api/types';
import Notice from '@components/Page/Notice';
import RiverItem from '@components/RiverItem';
import HeroTall from '@components/HeroTall';
import SimpleCardGrid from '@components/SimpleCardGrid';
import MarketingCardSection from '@components/MarketingCards/MarketingCardSection';
import CrftFeature from '@components/Home/CrftFeature';
import Hero from '@components/Hero';
import IconNav from '@components/IconNav';
import FAQSection from '@components/FAQSection';
import Testimonials from '@components/Testimonials';
import SectionWrapper from '@components/SectionWrapper';
import ContentWrapper from '@components/ContentWrapper';
import IconSection from '@components/IconSection';
import ButtonLink from '@components/ButtonLink';
import { ZENDESK_ARTICLES_URL } from '@constants';
import SupportSection from './SupportSection';
import SearchForm from './SearchForm';

type Props = {
  page: Page;
};

const SupportPage: FC<Props> = ({ page }) => {
  const supportContent = page.content.find(
    s => s._modelApiKey === 'support_section'
  );

  const topContent = page.content.filter(
    s =>
      s._modelApiKey === 'hero_primary' ||
      s._modelApiKey === 'page_notice_collection'
  );

  const restOfContent = page.content.filter(
    s =>
      s._modelApiKey !== 'hero_primary' &&
      s._modelApiKey !== 'support_section' &&
      s._modelApiKey !== 'markdown_content'
  );

  return (
    <>
      {topContent &&
        topContent.map(
          s =>
            s.id && (
              <Fragment key={s.id}>
                {(() => {
                  switch (s._modelApiKey) {
                    case 'page_notice_collection':
                      return (
                        <ContentWrapper>
                          <div className="bg-white">
                            <Notice {...s} pageNotice={s.pageNotice} />
                          </div>
                        </ContentWrapper>
                      );
                    case 'hero_primary':
                      return <Hero {...s} />;
                    default:
                      return null;
                  }
                })()}
              </Fragment>
            )
        )}
      <ContentWrapper className="mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="col-span-4 lg:col-span-8 lg:order-2">
            <SectionWrapper noSpacing>
              <SearchForm />
              <div className="flex justify-center mt-6">
                <ButtonLink
                  asButton
                  buttonStyle="secondary"
                  href={ZENDESK_ARTICLES_URL}
                  customMargin
                  internalLink
                  className="!w-auto !min-w-0 !justify-center inline-flex items-center"
                >
                  View all support articles
                </ButtonLink>
              </div>
              {restOfContent.map(
                (s, i) =>
                  s.id && (
                    <Fragment key={s.id}>
                      {(() => {
                        switch (s._modelApiKey) {
                          case 'river':
                            return <RiverItem {...s} isEven={i % 2 === 0} />;
                          case 'hero_tall':
                            return <HeroTall noContentWrapper {...s} />;
                          case 'crft_hero':
                            return <CrftFeature {...s} />;
                          case 'marketing_three_card_collection':
                            return (
                              <SectionWrapper>
                                <MarketingCardSection
                                  threeCardGrid
                                  {...s}
                                  marketingCardData={s.marketingCard}
                                />
                              </SectionWrapper>
                            );
                          case 'simple_card_grid':
                            return (
                              <SimpleCardGrid
                                simpleCardGridData={s.simpleCards}
                                heading={s.sectionHeading}
                                noContentWrapper
                                {...s}
                              />
                            );
                          case 'icon_nav_collection':
                            return <IconNav {...s} />;
                          case 'faq_section':
                            return <FAQSection {...s} />;
                          case 'testimonal_collection':
                            return (
                              <Testimonials testimonialsData={s.testimonials} />
                            );
                          case 'marketing_card2_x2_collection':
                            return (
                              <SectionWrapper data-test-id="marketing-card2-x2-block">
                                <MarketingCardSection
                                  {...s}
                                  marketingCardData={s.marketingCard}
                                />
                              </SectionWrapper>
                            );
                          case 'icon_block':
                            return (
                              <IconSection {...s} hasBg data={s.iconBlock} />
                            );
                          default:
                            return null;
                        }
                      })()}
                    </Fragment>
                  )
              )}
            </SectionWrapper>
          </div>
          {supportContent && (
            <div className="col-span-4 lg:order-1">
              <SupportSection
                heading={supportContent.sectionHeading}
                markdownFields={supportContent.markdownFields}
              />
            </div>
          )}
        </div>
      </ContentWrapper>
    </>
  );
};

export default memo(SupportPage);
